import { useState, useEffect, useCallback, useRef } from 'react';
import { thingsboardService } from '../services/thingsboard';
import { wsManager } from '../services/websocket';
import { TelemetryData } from './useTelemetry';

export function useZonesTelemetry(entityIds: string[], keys: string[], entityType: 'DEVICE' | 'ASSET' = 'ASSET') {
  // Map of zoneId -> TelemetryData
  const [zonesData, setZonesData] = useState<Record<string, TelemetryData>>({});
  const [loading, setLoading] = useState<boolean>(false);
  
  // Ref to store historical hourly power data: { [entityId]: { [hourTs]: value } }
  const hourlyDbRef = useRef<Record<string, Record<number, number>>>({});

  const needs24hPower = keys.includes('calculated_24h_power');
  
  // Real keys to fetch from TB
  const tbKeys = keys.filter(k => k !== 'calculated_24h_power');
  if (needs24hPower && !tbKeys.includes('hourly_power')) {
    tbKeys.push('hourly_power');
  }
  const keysString = tbKeys.join(',');

  const fetchInitialData = useCallback(async () => {
    if (!entityIds || entityIds.length === 0) return;
    setLoading(true);
    try {
      const promises = entityIds.map(async (entityId) => {
        try {
          const result = await thingsboardService.getLatestTelemetry(entityType, entityId, keysString);
          
          if (needs24hPower) {
             const now = Date.now();
             const startTs = now - 24 * 3600 * 1000;
             const histResult = await thingsboardService.getTimeseries(entityType, entityId, 'hourly_power', startTs, now, 0, 100, 'NONE');
             
             const hourlyMap: Record<number, number> = {};
             if (histResult && histResult['hourly_power']) {
                histResult['hourly_power'].forEach((pt: any) => {
                   const hourBucket = Math.floor(pt.ts / 3600000) * 3600000;
                   hourlyMap[hourBucket] = Number(pt.value);
                });
             }
             //console.log('hourlyMap', hourlyMap);
             hourlyDbRef.current[entityId] = hourlyMap;
             
             let sum24h = 0;
             Object.values(hourlyMap).forEach(v => { sum24h += v; });
             result['calculated_24h_power'] = [{ ts: now, value: sum24h.toString() }];
          }

          return { entityId, data: result };
        } catch (err) {
          console.error(`Failed to fetch initial telemetry for ${entityType} ${entityId}`, err);
          return { entityId, data: {} };
        }
      });
      
      const results = await Promise.all(promises);
      const newZonesData: Record<string, TelemetryData> = {};
      results.forEach((res) => {
        newZonesData[res.entityId] = res.data;
      });
      
      setZonesData(newZonesData);
    } catch (err) {
      console.error(`Failed to fetch ${entityType} telemetry`, err);
    } finally {
      setLoading(false);
    }
  }, [entityIds.join(','), keysString, entityType, needs24hPower]);

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
       fetchInitialData();
    }
    return () => {
      isMounted = false;
    };
  }, [fetchInitialData]);

  // Subscribe to real-time updates via WebSocket for all entities
  useEffect(() => {
    if (!entityIds || entityIds.length === 0) return;

    wsManager.connect();
    
    const subIds: number[] = [];
    
    entityIds.forEach((entityId) => {
      const subId = wsManager.subscribeTelemetry(entityType, entityId, keysString, (incomingData: any) => {
        setZonesData(prevData => {
          const prevZoneData = prevData[entityId] || {};
          const newZoneData = { ...prevZoneData };
          
          Object.keys(incomingData).forEach(key => {
            const values = incomingData[key].map((item: any[]) => ({
              ts: item[0],
              value: item[1]
            }));
            
            if (!newZoneData[key]) {
              newZoneData[key] = values;
            } else {
              newZoneData[key] = [...values, ...newZoneData[key]].slice(0, 5);
            }

            if (key === 'hourly_power' && needs24hPower) {
               const latestValue = values[0];
               if (latestValue && hourlyDbRef.current[entityId]) {
                 const hourBucket = Math.floor(latestValue.ts / 3600000) * 3600000;
                 hourlyDbRef.current[entityId][hourBucket] = Number(latestValue.value);
                 
                 const cutoff = Date.now() - 24 * 3600 * 1000;
                 let sum24h = 0;
                 Object.keys(hourlyDbRef.current[entityId]).forEach(bucketStr => {
                    const bTime = Number(bucketStr);
                    if (bTime < cutoff) {
                       delete hourlyDbRef.current[entityId][bTime];
                    } else {
                       sum24h += hourlyDbRef.current[entityId][bTime];
                    }
                 });

                 newZoneData['calculated_24h_power'] = [{ ts: latestValue.ts, value: sum24h.toString() }];
               }
            }
          });
          
          return {
            ...prevData,
            [entityId]: newZoneData
          };
        });
      });
      subIds.push(subId);
    });

    return () => {
      subIds.forEach(id => wsManager.unsubscribe(id));
    };
  }, [entityIds.join(','), keysString, entityType, needs24hPower]);

  return { zonesData, loading, refetch: fetchInitialData };
}
